import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { getMessages, refreshMessages, getCampaigns, getCampaignGroups, getMessagesTimeline, getMessageAttachment } from '../lib/api';
import { getMessagesCache, setMessagesCache } from '../lib/messagesCache';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Search, Loader2, History, X, Paperclip, FileImage, FileVideo, FileAudio, FileText, ChevronDown, ChevronRight, Megaphone, AlertTriangle } from 'lucide-react';

// Helper para calcular datas
const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (period) {
    case 'today':
      return { from: today.toISOString(), to: null };
    case 'week':
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay()); // Domingo
      return { from: weekStart.toISOString(), to: null };
    case 'month':
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: monthStart.toISOString(), to: null };
    case 'all':
    default:
      return { from: null, to: null };
  }
};

// Tempo (ms) que o cache é considerado "fresco". Dentro disso, reentrar na tela
// mostra o cache sem revalidar; acima disso, mostra o cache e revalida em background.
const MESSAGES_CACHE_TTL_MS = 20000;

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20); // Linhas visíveis por página
  const [phoneFilter, setPhoneFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sentByFilter, setSentByFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('week'); // Default: Esta semana
  const [typeFilter, setTypeFilter] = useState('all'); // Default: Todos
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [campaignGroups, setCampaignGroups] = useState([]);          // Fase 1: campanhas que batem nos filtros (backend agrupa)
  const [campaignGroupsTotal, setCampaignGroupsTotal] = useState(0); // total de campanhas (paginação server-side)
  const [campaignMessages, setCampaignMessages] = useState({});     // Fase 1: id -> {data,total,page,loading} (lazy no expand)
  const [timelineRows, setTimelineRows] = useState([]);             // Fase 3: aba Todos (campanhas + avulsas), paginado no servidor
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [loadingPercent, setLoadingPercent] = useState(0);
  
  const loadingTimerRef = useRef(null);
  const percentTimerRef = useRef(null);

  // Timer para contar segundos de carregamento e porcentagem animada
  useEffect(() => {
    if (loading) {
      setLoadingSeconds(0);
      setLoadingPercent(0);
      
      loadingTimerRef.current = setInterval(() => {
        setLoadingSeconds(prev => prev + 1);
      }, 1000);
      
      // Porcentagem animada - vai de 0 a 95 de forma suave
      percentTimerRef.current = setInterval(() => {
        setLoadingPercent(prev => {
          if (prev >= 95) return 95; // Para em 95% até carregar
          // Incremento desacelera conforme aumenta
          const increment = Math.max(0.5, (95 - prev) / 20);
          return Math.min(95, prev + increment);
        });
      }, 100);
    } else {
      // Quando termina, vai para 100%
      setLoadingPercent(100);
      
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
        percentTimerRef.current = null;
      }
      setLoadingSeconds(0);
    }
    
    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
      }
    };
  }, [loading]);

  // Mensagem de carregamento dinâmica
  const getLoadingMessage = () => {
    if (loadingSeconds < 60) {
      return 'Carregando lista...';
    } else if (loadingSeconds < 120) {
      return 'Lista grande, aguarde...';
    } else {
      return 'Você solicitou um histórico grande, ainda carregando. Se quiser, aplique outro filtro com menos resultados.';
    }
  };

  const handleOpenMessage = (message) => {
    setSelectedMessage(message);
    setDialogOpen(true);
  };

  const handleOpenAttachment = async (message) => {
    if (message.attachment_expired) {
      toast.error('Arquivo expirado (removido por purga)');
      return;
    }
    try {
      const response = await getMessageAttachment(message._id);
      const blob = new Blob([response.data], { type: message.attachment.content_type });
      const url = URL.createObjectURL(blob);
      
      // Criar link para download com nome original
      const link = document.createElement('a');
      link.href = url;
      link.download = message.attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 410 || detail.includes('purga')) {
        toast.error('Arquivo expirado (removido por purga)');
      } else {
        toast.error('Erro ao abrir anexo');
      }
    }
  };

  const getAttachmentIcon = (category) => {
    switch (category) {
      case 'image':
        return <FileImage className="w-4 h-4 text-blue-500" />;
      case 'video':
        return <FileVideo className="w-4 h-4 text-purple-500" />;
      case 'audio':
        return <FileAudio className="w-4 h-4 text-teal-500" />;
      case 'document':
        return <FileText className="w-4 h-4 text-orange-500" />;
      default:
        return <Paperclip className="w-4 h-4 text-gray-500" />;
    }
  };

  // Fase 1: lista as campanhas que têm mensagens batendo em TODOS os filtros ativos, paginada no
  // servidor (o backend agrupa; não baixa as mensagens). Loader cheio só na 1ª carga.
  const fetchCampaignGroups = async () => {
    const firstLoad = campaignGroups.length === 0;
    if (firstLoad) setLoading(true);
    try {
      const dateRange = getDateRange(periodFilter);
      const res = await getCampaignGroups({
        page,
        size: rowsPerPage,
        phone: phoneFilter || undefined,
        name: nameFilter || undefined,
        campaign: campaignFilter || undefined,
        status: statusFilter || undefined,
        sent_by: sentByFilter || undefined,
        date_from: (dateFromFilter || dateRange.from) || undefined,
        date_to: (dateToFilter || dateRange.to) || undefined,
      });
      setCampaignGroups(res.data.data || []);
      setCampaignGroupsTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
      setCampaignGroups([]);
      setCampaignGroupsTotal(0);
    } finally {
      if (firstLoad) setLoading(false);
    }
  };

  // Fase 3 (aba Todos): timeline unificada (campanhas + avulsas por data), paginada no servidor.
  const fetchTimeline = async () => {
    const firstLoad = timelineRows.length === 0;
    if (firstLoad) setLoading(true);
    try {
      const dateRange = getDateRange(periodFilter);
      const res = await getMessagesTimeline({
        page,
        size: rowsPerPage,
        phone: phoneFilter || undefined,
        name: nameFilter || undefined,
        campaign: campaignFilter || undefined,
        status: statusFilter || undefined,
        sent_by: sentByFilter || undefined,
        date_from: (dateFromFilter || dateRange.from) || undefined,
        date_to: (dateToFilter || dateRange.to) || undefined,
      });
      setTimelineRows(res.data.data || []);
      setTimelineTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
      setTimelineRows([]);
      setTimelineTotal(0);
    } finally {
      if (firstLoad) setLoading(false);
    }
  };

  // Fase 1: mensagens de UMA campanha, paginadas, carregadas ao expandir. Respeita os filtros de
  // MENSAGEM ativos (telefone/status/enviado-por/data); Nome aqui é da campanha (não vai como filtro).
  const loadCampaignMessages = async (campaignId, cpage) => {
    setCampaignMessages(prev => ({ ...prev, [campaignId]: { ...(prev[campaignId] || { data: [], total: 0 }), page: cpage, loading: true } }));
    try {
      const dateRange = getDateRange(periodFilter);
      const res = await getMessages({
        campaign: campaignId,
        page: cpage,
        size: rowsPerPage,
        phone: phoneFilter || undefined,
        name: nameFilter || undefined,
        status: statusFilter || undefined,
        sent_by: sentByFilter || undefined,
        date_from: (dateFromFilter || dateRange.from) || undefined,
        date_to: (dateToFilter || dateRange.to) || undefined,
      });
      setCampaignMessages(prev => ({ ...prev, [campaignId]: { data: res.data.data, total: res.data.total, page: cpage, loading: false } }));
    } catch (error) {
      setCampaignMessages(prev => ({ ...prev, [campaignId]: { ...(prev[campaignId] || { data: [], total: 0 }), loading: false } }));
    }
  };

  const toggleCampaign = (campaignId) => {
    const willExpand = !expandedCampaigns[campaignId];
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
    // Abas Campanhas e Todos: ao expandir pela 1ª vez, busca as mensagens daquela campanha sob demanda.
    if (willExpand && (typeFilter === 'campaigns' || typeFilter === 'all') && !campaignMessages[campaignId]) {
      loadCampaignMessages(campaignId, 0);
    }
  };

  // (O agrupamento client-side do "Todos" foi substituído pela timeline paginada no servidor — Fase 3.)

  // Todos os modos agora vêm paginados do backend: Mensagens (flat), Campanhas (grupos) e Todos (timeline).
  const isServerMode = typeFilter === 'messages';
  const isCampaignsMode = typeFilter === 'campaigns';
  const isAllMode = typeFilter === 'all';

  // Aba Campanhas: itens = grupos que o backend já filtrou (por TODOS os filtros) e paginou.
  // "N mensagens" = quantidade de mensagens que BATEM no filtro; sent/failed/pending idem.
  const campaignItems = useMemo(() => {
    if (!isCampaignsMode) return [];
    return campaignGroups.map(g => ({
      type: 'campaign',
      lazy: true,
      campaign_id: g._id,
      campaign_name: g.campaign_name || 'Campanha sem nome',
      total_contacts: g.count || 0,
      sent: g.sent || 0,
      failed: g.failed || 0,
      pending: g.pending || 0,
      allFinalized: (g.pending || 0) === 0,
      latest_date: g.latest_date,
      attachment: g.attachment,
      attachment_expired: g.attachment_expired,
    }));
  }, [isCampaignsMode, campaignGroups]);

  // Aba Todos: itens = linhas da timeline (backend). Campanha vira linha LAZY; avulsa vira mensagem.
  const timelineItems = useMemo(() => {
    if (!isAllMode) return [];
    return timelineRows.map(r => (
      r.type === 'campaign'
        ? {
            type: 'campaign',
            lazy: true,
            campaign_id: r.campaign_id,
            campaign_name: r.campaign_name || 'Campanha sem nome',
            total_contacts: r.count || 0,
            sent: r.sent || 0,
            failed: r.failed || 0,
            pending: r.pending || 0,
            allFinalized: (r.pending || 0) === 0,
            latest_date: r.latest_date,
            attachment: r.attachment,
            attachment_expired: r.attachment_expired,
          }
        : { ...r, type: 'individual' }
    ));
  }, [isAllMode, timelineRows]);

  // Paginação: total/página do backend em todos os modos.
  const totalRows = isServerMode ? total : isCampaignsMode ? campaignGroupsTotal : timelineTotal;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const rowsLabel = isCampaignsMode ? `${totalRows} campanhas` : isAllMode ? `${totalRows} itens` : `${totalRows} mensagens`;
  const paginatedRows = useMemo(() => {
    if (isServerMode) {
      // backend já devolveu só esta página; todas são individuais (sem campaign_id)
      return messages.map(m => ({ ...m, type: 'individual' }));
    }
    if (isCampaignsMode) {
      return campaignItems;   // já é a página atual (paginado no servidor)
    }
    return timelineItems;     // aba Todos: já é a página atual (paginado no servidor)
  }, [isServerMode, isCampaignsMode, messages, campaignItems, timelineItems]);

  // Fetch messages com cache em memória (stale-while-revalidate).
  // - cache hit: mostra na hora (sem loader) e revalida em background se estiver "velho";
  // - cache miss: mostra loader, busca e cacheia.
  // O cache é invalidado em login/logout/impersonação (ver AuthContext).
  const fetchMessages = async ({ useCache = true, background = false } = {}) => {
    // Calcular datas baseado no período selecionado
    const dateRange = getDateRange(periodFilter);
    const effectiveDateFrom = dateFromFilter || dateRange.from;
    const effectiveDateTo = dateToFilter || dateRange.to;

    // Modo servidor (aba Mensagens): pede a página atual com size da tela. Demais abas: baixa
    // o conjunto (size grande) e pagina no cliente. NÃO resetamos page aqui — quem troca filtro
    // já chama setPage(0); resetar aqui quebraria a navegação de página no modo servidor.
    const reqPage = isServerMode ? page : 0;
    const reqSize = isServerMode ? rowsPerPage : 10000;

    const params = {
      phone: phoneFilter || undefined,
      name: nameFilter || undefined,
      campaign: campaignFilter || undefined,
      status: statusFilter || undefined,
      sent_by: sentByFilter || undefined,
      date_from: effectiveDateFrom || undefined,
      date_to: effectiveDateTo || undefined,
      msg_type: typeFilter !== 'all' ? typeFilter : undefined,
    };
    // page/size entram na chave: no modo servidor cada página é um cache distinto.
    const cacheKey = JSON.stringify({ ...params, page: reqPage, size: reqSize });

    if (useCache && !background) {
      const cached = getMessagesCache(cacheKey);
      if (cached) {
        setMessages(cached.data);
        setTotal(cached.total);
        setLoading(false);
        // Revalida em segundo plano só se o cache já passou do TTL.
        if (Date.now() - cached.ts > MESSAGES_CACHE_TTL_MS) {
          fetchMessages({ useCache: false, background: true });
        }
        return;
      }
    }

    if (!background) {
      setLoading(true);
    }
    try {
      const response = await getMessages({ page: reqPage, size: reqSize, ...params });
      setMessages(response.data.data);
      setTotal(response.data.total);
      setMessagesCache(cacheKey, response.data.data, response.data.total);
    } catch (error) {
      if (!background) toast.error('Erro ao carregar mensagens');
    } finally {
      if (!background) setLoading(false);
    }
  };

  // Carregar quando filtros ou página mudam. No modo servidor (aba Mensagens) a mudança de
  // `page` refaz o fetch; nos demais modos `reqPage` é fixo em 0 → vira cache hit (sem rede).
  useEffect(() => {
    // Cada aba tem seu fetch, todos paginados no servidor:
    if (typeFilter === 'campaigns') {
      fetchCampaignGroups();          // Fase 1: grupos de campanha
    } else if (typeFilter === 'all') {
      fetchTimeline();                // Fase 3: timeline unificada
    } else {
      fetchMessages();                // Fase 2: mensagens avulsas (flat)
    }
  }, [phoneFilter, nameFilter, campaignFilter, statusFilter, sentByFilter, periodFilter, dateFromFilter, dateToFilter, typeFilter, page]);

  // Fase 1: mudou um filtro → recolhe campanhas e limpa o cache das expandidas (evita mensagens stale).
  useEffect(() => {
    setExpandedCampaigns({});
    setCampaignMessages({});
  }, [phoneFilter, nameFilter, campaignFilter, statusFilter, sentByFilter, dateFromFilter, dateToFilter, periodFilter]);

  useEffect(() => {
    fetchCampaigns();
    fetchUsers();
  }, []);

  // Auto-refresh pending messages (queued, sent) every 5 seconds
  // Atualiza a UI automaticamente quando há mudanças de status (apenas atualização local, sem reload)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const response = await refreshMessages(true);
        const { updated, updated_statuses } = response.data;
        
        // Se houve atualizações, atualizar apenas os status localmente (sem reload completo)
        if (updated > 0 && updated_statuses) {
          setMessages(prevMessages => {
            // Só atualiza se prevMessages não estiver vazio
            if (prevMessages.length === 0) return prevMessages;
            
            return prevMessages.map(msg => {
              if (updated_statuses[msg._id]) {
                return { ...msg, delivery_status: updated_statuses[msg._id] };
              }
              return msg;
            });
          });
        }
      } catch (error) {
        console.error('Erro ao atualizar status automaticamente:', error);
      }
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await getCampaigns({ page: 0, size: 100 });
      setAvailableCampaigns(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/senders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvailableUsers(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleClearFilters = () => {
    setPhoneFilter('');
    setNameFilter('');
    setCampaignFilter('');
    setStatusFilter('');
    setSentByFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setPage(0);
  };

  const getStatusBadge = (status) => {
    // Normalize sent/delivered/read to "Enviada" (all successful)
    const normalizedStatus = ['sent', 'delivered', 'read'].includes(status) ? 'sent' : status;
    
    const styles = {
      queued: 'bg-orange-100 text-orange-800',
      sent: 'bg-teal-100 text-teal-800',
      failed: 'bg-red-100 text-red-800',
      unknown: 'bg-slate-200 text-slate-800',
    };
    
    const labels = {
      queued: 'Na fila',
      sent: 'Enviada',
      failed: 'Falha',
      unknown: 'Desconhecido',
    };
    
    return (
      <Badge className={styles[normalizedStatus] || styles.unknown}>
        {labels[normalizedStatus] || labels.unknown}
      </Badge>
    );
  };

  // Format date to Brasilia timezone
  const formatDateBrasilia = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Parse the date (MongoDB returns ISO string)
      const date = new Date(dateString);
      
      // Check if valid date
      if (isNaN(date.getTime())) return dateString;
      
      // Format to Brasilia timezone
      const formatter = new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      return formatter.format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const periodButtons = [
    { value: 'today', label: 'Hoje' },
    { value: 'week', label: 'Esta semana' },
    { value: 'month', label: 'Este mês' },
    { value: 'all', label: 'Todos' },
  ];

  const typeButtons = [
    { value: 'all', label: 'Todos' },
    { value: 'messages', label: 'Mensagens' },
    { value: 'campaigns', label: 'Campanhas' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Period & Type Quick Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm text-muted-foreground mr-2">Período:</span>
        {periodButtons.map((btn) => (
          <Button
            key={btn.value}
            variant={periodFilter === btn.value ? "default" : "outline"}
            size="sm"
            onClick={() => { setPeriodFilter(btn.value); setPage(0); }}
            className="h-8"
          >
            {btn.label}
          </Button>
        ))}
        <span className="text-sm text-muted-foreground ml-4 mr-2">Tipo:</span>
        {typeButtons.map((btn) => (
          <Button
            key={btn.value}
            variant={typeFilter === btn.value ? "default" : "outline"}
            size="sm"
            onClick={() => { setTypeFilter(btn.value); setPage(0); }}
            className="h-8"
          >
            {btn.label}
          </Button>
        ))}
      </div>

      {/* Filters */}
      <div className="sticky top-0 z-20 bg-background pb-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-0">
        <div className="flex items-center gap-3">
          {/* Phone Filter */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Telefone..."
              value={phoneFilter}
              onChange={(e) => { setPhoneFilter(e.target.value); setPage(0); }}
              className="pl-9"
              data-testid="history-phone-filter-input"
            />
          </div>

          {/* Name Filter */}
          <div className="flex-1">
            <Input
              placeholder="Nome..."
              value={nameFilter}
              onChange={(e) => { setNameFilter(e.target.value); setPage(0); }}
              data-testid="history-name-filter-input"
            />
          </div>

          {/* Campaign Filter */}
          <div className="flex-1">
            <Select value={campaignFilter} onValueChange={(value) => { setCampaignFilter(value); setPage(0); }}>
              <SelectTrigger data-testid="history-campaign-filter">
                <SelectValue placeholder="Campanha" />
              </SelectTrigger>
              <SelectContent>
                {availableCampaigns.map((campaign) => (
                  <SelectItem key={campaign._id} value={campaign._id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="flex-1">
            <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(0); }}>
              <SelectTrigger data-testid="history-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sent">Enviada</SelectItem>
                <SelectItem value="failed">Falha</SelectItem>
                <SelectItem value="queued">Na fila</SelectItem>
                <SelectItem value="unknown">Desconhecido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sent By Filter */}
          <div className="flex-1">
            <Select value={sentByFilter} onValueChange={(value) => { setSentByFilter(value); setPage(0); }}>
              <SelectTrigger data-testid="history-sent-by-filter">
                <SelectValue placeholder="Enviado por" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user) => (
                  <SelectItem key={user._id} value={user._id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date From Filter */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">De</span>
            <Input
              type="date"
              value={dateFromFilter}
              onChange={(e) => { setDateFromFilter(e.target.value); setPage(0); }}
              title="Data inicial"
              data-testid="history-date-from-filter"
              className="dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Date To Filter */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Até</span>
            <Input
              type="date"
              value={dateToFilter}
              onChange={(e) => { setDateToFilter(e.target.value); setPage(0); }}
              title="Data final"
              data-testid="history-date-to-filter"
              className="dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Buttons */}
          <Button 
            variant="secondary" 
            onClick={handleClearFilters}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>
        </div>
      </div>

      {/* Loading State - Centralizado */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 p-8">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {Math.round(loadingPercent)}%
                </span>
              </div>
            </div>
            <p className={`text-muted-foreground text-center ${loadingSeconds >= 120 ? 'max-w-md' : ''}`}>
              {getLoadingMessage()}
            </p>
            {loadingSeconds >= 60 && (
              <p className="text-xs text-muted-foreground">
                {Math.floor(loadingSeconds / 60)}:{(loadingSeconds % 60).toString().padStart(2, '0')} decorridos
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Table */}
          <Card className="flex-1 flex flex-col min-h-0 mb-4 mt-4">
            <CardContent className="p-0 flex-1 flex flex-col min-h-0">
              <Table data-testid="history-table" className="table-fixed">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="w-[15%]">Nome</TableHead>
                    <TableHead className="w-[12%]">Telefone</TableHead>
                    <TableHead className="w-[30%]">Mensagem</TableHead>
                    <TableHead className="w-[60px] text-center">Anexo</TableHead>
                    <TableHead className="w-[10%] text-center">Status</TableHead>
                    <TableHead className="w-[12%]">Enviado por</TableHead>
                    <TableHead className="w-[15%]">Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(paginatedRows.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="empty-state">
                      <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">{isCampaignsMode ? 'Nenhuma campanha encontrada' : 'Nenhuma mensagem encontrada'}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {/* Lista paginada em ordem cronológica */}
                  {paginatedRows.map((item) => (
                    item.type === 'campaign' ? (
                      <React.Fragment key={`campaign-${item.campaign_id}`}>
                        {/* Linha da campanha (colapsável) */}
                        <TableRow 
                          className="bg-primary/5 hover:bg-primary/10 cursor-pointer border-l-4 border-l-primary"
                          onClick={() => toggleCampaign(item.campaign_id)}
                        >
                          <TableCell colSpan={4} className="py-3">
                            <div className="flex items-center gap-3">
                              {expandedCampaigns[item.campaign_id] ? (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                              <Megaphone className="w-4 h-4 text-primary" />
                              <span className="font-medium">{item.campaign_name}</span>
                              <Badge variant="secondary" className="ml-2">
                                {item.lazy ? item.total_contacts : item.messages.length} mensagens
                              </Badge>
                              {item.allFinalized ? (
                                <>
                                  <Badge className="bg-teal-100 text-teal-800">
                                    {item.sent} enviadas
                                  </Badge>
                                  {item.failed > 0 && (
                                    <Badge className="bg-red-100 text-red-800">
                                      {item.failed} falhas
                                    </Badge>
                                  )}
                                </>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800">
                                  {item.pending} processando
                                </Badge>
                              )}
                              {item.attachment && (
                                item.attachment_expired ? (
                                  <Badge className="bg-amber-100 text-amber-800">
                                    anexo expirado
                                  </Badge>
                                ) : (
                                  <Badge className="bg-blue-100 text-blue-800">
                                    com anexo
                                  </Badge>
                                )
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3"></TableCell>
                          <TableCell className="py-3"></TableCell>
                          <TableCell className="py-3 text-muted-foreground">
                            {formatDateBrasilia(item.latest_date)}
                          </TableCell>
                        </TableRow>
                        
                        {/* Mensagens da campanha (expandidas) */}
                        {expandedCampaigns[item.campaign_id] && (() => {
                          const cm = campaignMessages[item.campaign_id];
                          const msgs = item.lazy ? (cm?.data || []) : item.messages;
                          const cmPages = item.lazy && cm ? Math.ceil(cm.total / rowsPerPage) : 1;
                          return (
                            <>
                              {item.lazy && (!cm || cm.loading) && (
                                <TableRow className="bg-primary/[0.02]">
                                  <TableCell colSpan={7} className="py-4 pl-10 text-sm text-muted-foreground">
                                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                    Carregando mensagens...
                                  </TableCell>
                                </TableRow>
                              )}
                              {msgs.map((msg) => (
                          <TableRow
                            key={msg._id}
                            className="cursor-pointer hover:bg-primary/5 bg-primary/[0.02]"
                            onClick={() => handleOpenMessage(msg)}
                          >
                            <TableCell className="text-muted-foreground py-2 pl-10">
                              {msg.contact_name || '—'}
                            </TableCell>
                            <TableCell className="font-medium py-2">{msg.phone}</TableCell>
                            <TableCell className="max-w-xs truncate py-2">{msg.message}</TableCell>
                            <TableCell className="py-2 text-center">
                              {msg.attachment ? (
                                msg.attachment_expired ? (
                                  <span className="text-xs text-amber-600" title="Arquivo expirado (removido por purga)">Expirado</span>
                                ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenAttachment(msg);
                                  }}
                                  className="p-1 rounded hover:bg-muted transition-colors"
                                  title={msg.attachment.filename}
                                >
                                  {getAttachmentIcon(msg.attachment.category)}
                                </button>
                                )
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 text-center">{getStatusBadge(msg.delivery_status)}</TableCell>
                            <TableCell className="text-muted-foreground py-2">
                              {msg.sent_by_name || '—'}
                            </TableCell>
                            <TableCell className="text-muted-foreground py-2">
                              {formatDateBrasilia(msg.created_at)}
                            </TableCell>
                          </TableRow>
                              ))}
                              {item.lazy && cm && !cm.loading && cmPages > 1 && (
                                <TableRow className="bg-primary/[0.02]">
                                  <TableCell colSpan={7} className="py-2 pl-10">
                                    <div className="flex items-center gap-3 text-sm">
                                      <Button variant="outline" size="sm" disabled={cm.page === 0}
                                        onClick={(e) => { e.stopPropagation(); loadCampaignMessages(item.campaign_id, cm.page - 1); }}>
                                        Anterior
                                      </Button>
                                      <span className="text-muted-foreground">
                                        Página {cm.page + 1} de {cmPages} ({cm.total} mensagens)
                                      </span>
                                      <Button variant="outline" size="sm" disabled={cm.page >= cmPages - 1}
                                        onClick={(e) => { e.stopPropagation(); loadCampaignMessages(item.campaign_id, cm.page + 1); }}>
                                        Próxima
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          );
                        })()}
                      </React.Fragment>
                    ) : (
                      /* Mensagem individual */
                      <TableRow 
                        key={item._id} 
                        className="cursor-pointer hover:bg-primary/5"
                        onClick={() => handleOpenMessage(item)}
                      >
                        <TableCell className="text-muted-foreground py-2">
                          {item.contact_name || '—'}
                        </TableCell>
                        <TableCell className="font-medium py-2">{item.phone}</TableCell>
                        <TableCell className="max-w-xs truncate py-2">{item.message}</TableCell>
                        <TableCell className="py-2 text-center">
                          {item.attachment ? (
                            item.attachment_expired ? (
                              <span className="text-xs text-amber-600" title="Arquivo expirado (removido por purga)">Expirado</span>
                            ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAttachment(item);
                              }}
                              className="p-1 rounded hover:bg-muted transition-colors"
                              title={item.attachment.filename}
                            >
                              {getAttachmentIcon(item.attachment.category)}
                            </button>
                            )
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-center">{getStatusBadge(item.delivery_status)}</TableCell>
                        <TableCell className="text-muted-foreground py-2">
                          {item.sent_by_name || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground py-2">
                          {formatDateBrasilia(item.created_at)}
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 py-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages} ({rowsLabel})
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
      
      {totalPages <= 1 && totalRows > 0 && (
        <div className="flex justify-center py-4">
          <span className="text-sm text-muted-foreground">
            {rowsLabel}
          </span>
        </div>
      )}
        </>
      )}

      {/* Message Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Mensagem</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Contato</p>
                  <p className="font-medium">{selectedMessage.contact_name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Telefone</p>
                  <p className="font-medium">{selectedMessage.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(selectedMessage.delivery_status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data</p>
                  <p className="font-medium">{formatDateBrasilia(selectedMessage.created_at)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-1">Mensagem</p>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">{selectedMessage.message}</div>
              </div>

              {selectedMessage.campaign_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Campanha</p>
                  <p className="font-medium">{selectedMessage.campaign_name}</p>
                </div>
              )}

              {selectedMessage.attachment && (
                <div className="overflow-hidden">
                  <p className="text-sm text-muted-foreground mb-2">Anexo</p>
                  {selectedMessage.attachment_expired ? (
                    <div className="w-full border border-amber-200 rounded-lg p-3 bg-amber-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        </div>
                        <div className="flex-1 overflow-hidden" style={{ minWidth: 0, width: 0 }}>
                          <p className="text-sm font-medium text-amber-700">Arquivo expirado (removido por purga)</p>
                          <p className="text-xs text-amber-600 truncate">{selectedMessage.attachment.filename}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                  <button
                    onClick={() => handleOpenAttachment(selectedMessage)}
                    className="w-full text-left border border-gray-200 rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden"
                    style={{ maxWidth: '100%' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {getAttachmentIcon(selectedMessage.attachment.category)}
                      </div>
                      <div className="flex-1 overflow-hidden" style={{ minWidth: 0, width: 0 }}>
                        <p className="text-sm font-medium truncate" title={selectedMessage.attachment.filename}>
                          {selectedMessage.attachment.filename}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {(selectedMessage.attachment.size / 1024).toFixed(1)} KB • {selectedMessage.attachment.category}
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </button>
                  )}
                  {!selectedMessage.attachment_expired && <p className="text-xs text-muted-foreground mt-1">Clique para abrir o anexo</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
